import type { ManagerProfile } from "@averagejoesff/shared";
import { AppLink } from "../../components/AppLink";
import { formatRecord } from "../../lib/format";
import { ManagerAvatar } from "./ManagerAvatar";
import { profilePath } from "./people";

interface ManagerGridProps {
  profiles: readonly ManagerProfile[];
  myId: string | null;
}

/** Current managers first, then former ones, each by all-time wins. */
export function ManagerGrid({ profiles, myId }: ManagerGridProps) {
  const byWins = (a: ManagerProfile, b: ManagerProfile) => b.regularSeason.wins - a.regularSeason.wins || a.name.localeCompare(b.name);
  const current = profiles.filter((p) => p.active).sort(byWins);
  const former = profiles.filter((p) => !p.active).sort(byWins);

  return (
    <>
      <List profiles={current} myId={myId} />
      {former.length > 0 && (
        <>
          <h3 className="feature__subhead">Former managers</h3>
          <List profiles={former} myId={myId} />
        </>
      )}
    </>
  );
}

const winPercent = (p: ManagerProfile) => {
  const { wins, losses, ties } = p.regularSeason;
  const games = wins + losses + ties;
  return games === 0 ? null : Math.round(((wins + ties / 2) / games) * 100);
};

function List({ profiles, myId }: ManagerGridProps) {
  return (
    <ul className="manager-grid">
      {profiles.map((p) => {
        const pct = winPercent(p);
        return (
          <li key={p.personId}>
            <AppLink href={profilePath(p.personId)} className="manager-card">
              <ManagerAvatar name={p.name} logoUrl={p.logoUrl} />
              <span className="manager-card__name">
                {p.name}
                {p.personId === myId && <span className="tag tag--you">You</span>}
              </span>
              <span className="manager-card__record">{formatRecord(p.regularSeason.wins, p.regularSeason.losses, p.regularSeason.ties)}</span>
              <span className="manager-card__meta">
                {pct !== null && `${pct}% wins · `}
                {p.seasons.length} {p.seasons.length === 1 ? "season" : "seasons"}
              </span>
              {/* Always rendered, so a card with no titles is as tall as one with several. */}
              <span
                className="manager-card__titles"
                aria-label={
                  p.titles.length > 0 ? `${p.titles.length} championships: ${p.titles.join(", ")}` : "No championships"
                }
              >
                {p.titles.length > 0 ? `🏆 ${p.titles.length > 2 ? `×${p.titles.length}` : p.titles.join(", ")}` : "No titles yet"}
              </span>
            </AppLink>
          </li>
        );
      })}
    </ul>
  );
}
